import { ReactElement } from 'react';
import { DashboardSetting, SortByValue } from '@Apis/companyDashboard';

export interface Tab {
    id: number;
    label: string;
    component?: ReactElement;
    icon?: string;
    selectedProjectIds?: number[];
    selectedSectionIds?: string[];
    projectScope?: 'all' | 'selected';
    projectNames?: string[];
}

export interface TaskGraphDataPoint {
    id: string;
    value: number;
    urlParams: Record<string, string[]>;
}

export interface MilestoneItem {
    id: number;
    name: string;
    targetDate: string;
    statusId: number;
    statusName: string;
    locationKey: string;
    locationTaskId: number;
}

export interface LocationSummaryData {
    id: number;
    name: string;
    abbreviation: string;
    locationKey: string;
    // Metrics — returned by POST overview-tab/{id}
    overallCompletionPct?: number;
    pastDueCount?: number;
    totalTaskCount?: number;
    taskGraph?: TaskGraphDataPoint[];
    overallCompleteUrlParams?: Record<string, string[]>;
    pastDueUrlParams?: Record<string, string[]>;
    totalTasksUrlParams?: Record<string, string[]>;
    pastDueMilestoneCount?: number;
    completeToDatePct?: number;
    upcomingMilestoneCount?: number;
    pastDueMilestoneUrlParams?: Record<string, string[]>;
    upcomingMilestoneUrlParams?: Record<string, string[]>;
    pastDueMilestonesUrlParams?: Record<string, string[]>;
    upcomingMilestonesUrlParams?: Record<string, string[]>;
    upcomingUnassignedCount?: number;
    totalUnassignedCount?: number;
    completeToDateUrlParams?: Record<string, string[]>;
    zeroDate?: string | null;
    // Health score: 0-10 (higher is better). Risk score: 0-10 magnitude (higher is worse). Null when the active filters do not support scoring.
    healthScore?: number | null;
    riskScore?: number | null;
    scoresUnavailableReason?: string | null;
}

export interface LocationSortField {
    label: string;
    value: SortByValue;
    disabled?: boolean;
    ascLabel: string;
    descLabel: string;
}
const LOCATION_SORT_FIELD_BY_VALUE: Record<SortByValue, LocationSortField> = {
    healthScore: { label: 'Health Score', value: 'healthScore', ascLabel: 'Best first', descLabel: 'Worst first', disabled: true },
    riskScore: { label: 'Risk Score', value: 'riskScore', ascLabel: 'Lowest first', descLabel: 'Highest first', disabled: true },
    zeroDate: { label: 'Zero Date', value: 'zeroDate', ascLabel: 'Earliest first', descLabel: 'Latest first' },
    name: { label: 'Name', value: 'name', ascLabel: 'A to Z', descLabel: 'Z to A' },
};

export const LOCATION_SORT_FIELDS: LocationSortField[] = [
    LOCATION_SORT_FIELD_BY_VALUE.healthScore,
    LOCATION_SORT_FIELD_BY_VALUE.riskScore,
    LOCATION_SORT_FIELD_BY_VALUE.zeroDate,
    LOCATION_SORT_FIELD_BY_VALUE.name,
];

// A saved/server DashboardSetting can carry a schema-valid sortBy (e.g. 'healthScore') that's
// still a disabled field in LOCATION_SORT_FIELDS (no backing data yet). Reset it to 'name' here,
// at the point settings are loaded, so a disabled field never reaches the overview-tab request —
// not just at the sort-direction toggle, which only blocks further interaction once already there.
export const clampDisabledSortBy = (settings: DashboardSetting): DashboardSetting => {
    const field = LOCATION_SORT_FIELDS.find((f) => f.value === settings.sortBy);
    return field?.disabled ? { ...settings, sortBy: 'name' } : settings;
};

export interface LocationDetailData {
    id: number;
    name: string;
    locationKey: string;
    abbreviation: string;
    brandName: string;
    areaName: string;
    continentName: string;
    unit: string;
    managementCompanyName: string;
    regionName: string;
    typeName: string;
}

export interface OverviewFilters {
    disciplineIds?: number[];
    departmentIds?: number[];
    phaseIds?: number[];
    communityIds?: number[];
    baselineIds?: number[];
    tagIds?: number[];
    locationIds?: string[];
    brandIds?: number[];
    areaIds?: number[];
    regionIds?: number[];
    managementCompanyIds?: number[];
    locationTypeIds?: number[];
    continentIds?: number[];
}
